import jsonpath from 'jsonpath';
import {
  DevHubAutocomplete,
  DevHubFormControl,
  DevHubFormHelperText,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { useEffect, useState } from 'react';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { md5Hash } from '../../util/md5.mjs';
import useDebounce from 'react-use/lib/useDebounce';
import { cacheGet, cacheSet } from '../../util/persistedState';
import { UIOptionsType } from '@rjsf/utils';
import { ConfigHelper, isRelativeUrl } from '@devxdiscover/devhub-common';

export type ApiDropdownOptions = UIOptionsType & {
  labelText: string;
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: string;
  headers?: Record<string, string>;
  valueJsonPath: string;
  cacheTtlSeconds: number;
  sortResults: 'None' | 'Ascending' | 'Descending';
  unique: boolean;
};

/**
 * The schema for the {@link ApiDropdownComponent} properties.
 */
export const ApiDropdownComponentSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      label: {
        title: 'Label',
        type: 'string',
        description: 'The label to display for the field',
      },
      url: {
        title: 'URL',
        type: 'string',
        description: 'The URL to fetch the options from',
      },
      cacheTtlSeconds: {
        title: 'Cache TTL (seconds)',
        type: 'number',
        description:
          'The number of seconds to cache the options for (default: 300)',
        default: 300,
      },
      sortResults: {
        title: 'Sort Results',
        enum: ['None', 'Ascending', 'Descending'],
        default: 'None',
        description: 'The sort order of the results',
      },
      unique: {
        title: 'Make Unique',
        type: 'boolean',
        default: true,
        description:
          'If true, make the results of the JSON Path unique; otherwise do not',
      },
      method: {
        title: 'Method',
        type: 'string',
        enum: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
        default: 'GET',
        description: 'The HTTP method to use when fetching the options',
      },
      body: {
        title: 'Body',
        type: 'string',
        description: 'The body to send with the request',
      },
      headers: {
        title: 'Headers',
        type: 'array',
        items: {
          type: 'object',
          properties: {
            name: {
              title: 'Name',
              type: 'string',
            },
            value: {
              title: 'Value',
              type: 'string',
            },
          },
        },
      },
      valueJsonPath: {
        title: 'Value JSONPath',
        type: 'string',
        description:
          'The JSONPath to use to extract the value from the response',
      },
    },
  },
  returnValue: {
    type: 'string',
  },
};

const fetchOptions = async (
  options: ApiDropdownOptions,
  controller: AbortController,
  valuePath: string,
): Promise<string[]> => {
  const { url, method, headers } = options;
  const cacheKey = md5Hash(JSON.stringify({ url, method, headers }));
  const cacheResult = cacheGet<string>(cacheKey);
  const resolvedUrl = isRelativeUrl(url)
    ? `${ConfigHelper.instance.backendUrl}${url}`
    : url;
  try {
    let data: string = '';
    if (!cacheResult) {
      const response = await fetch(resolvedUrl, {
        method,
        headers,
        body: options.body,
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(
          `Failed to fetch options from ${resolvedUrl} with status ${response.status}`,
        );
      }
      data = await response.json();
      cacheSet(cacheKey, data, { seconds: options.cacheTtlSeconds });
    } else {
      data = cacheResult;
    }
    let results = jsonpath.query(data, valuePath);
    if (options.unique ?? true) {
      // Make the array unique without changing the order
      results = results.filter(
        (value, index, self) => self.indexOf(value) === index,
      );
    }
    switch (options.sortResults) {
      case 'Ascending':
        return results.sort((a, b) => a.localeCompare(b));
      case 'Descending':
        return results.sort((a, b) => b.localeCompare(a));
      default:
        return results;
    }
  } catch (err: any) {
    if (err.name === 'AbortError') {
      // Aborted; not an error
      return [];
    }
    throw err;
  }
};
/**
 * This component pulls a list of options from an API endpoint and allows users to select one.
 * @param props - The properties passed to the component.
 */
export const ApiDropdownComponent = (
  props: FieldExtensionComponentProps<string, ApiDropdownOptions>,
) => {
  const {
    required,
    uiSchema,
    schema,
    rawErrors,
    idSchema,
    onChange,
    formData,
    formContext,
  } = props;
  const opts = uiSchema['ui:options'] ?? ({} as ApiDropdownOptions);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<string[]>([]);
  const [valuePath, setValuePath] = useState<string | undefined>(undefined);

  useDebounce(
    () => {
      // If the JSONPath contains a variable placeholder, replace it with the corresponding value from the form context
      if (opts.valueJsonPath && opts.valueJsonPath.includes('{{')) {
        setValuePath(
          opts.valueJsonPath.replace(/\{\{(\w+)}}/g, (_match, variableName) => {
            if (!formContext.formData || !formContext.formData[variableName]) {
              return _match;
            }
            // Replace the variable placeholder with the corresponding variable value
            return formContext.formData[variableName];
          }),
        );
      } else {
        setValuePath(opts.valueJsonPath);
      }
    },
    500,
    [formContext.formData],
  );

  useEffect(() => {
    if (!opts.url) {
      return undefined;
    }
    if (!valuePath || valuePath.includes('{{')) {
      return undefined;
    }

    setLoading(true);
    const controller = new AbortController();
    (async () => {
      const dropdownOptions = await fetchOptions(opts, controller, valuePath);
      setOptions(dropdownOptions);
      if (formData && !dropdownOptions.includes(formData)) {
        onChange(undefined);
      }
      setLoading(false);
    })();
    return () => {
      setLoading(false);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.url, opts.method, opts.headers, valuePath]);
  return (
    <DevHubFormControl
      margin="dense"
      required={required}
      error={rawErrors?.length > 0}
    >
      <DevHubAutocomplete
        id={idSchema?.$id}
        aria-describedby={`${idSchema?.$id}desc`}
        loading={loading}
        options={options ?? []}
        value={formData && options.includes(formData) ? formData : null}
        onChange={(_, value) => onChange(value ?? undefined)}
        loadingText="Loading..."
        autoSelect
        renderInput={params => (
          <DevHubTextField
            {...params}
            label={opts.labelText ?? 'Select an option'}
            margin="normal"
            variant="outlined"
            required={required}
            InputProps={params.InputProps}
          />
        )}
      />
      {schema.description && (
        <DevHubFormHelperText id={`${idSchema?.$id}desc`}>
          {schema.description}
        </DevHubFormHelperText>
      )}
    </DevHubFormControl>
  );
};
